import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Hotel, Download, Upload, CheckCircle2, XCircle } from "lucide-react";
import * as XLSX from "xlsx";

interface ParsedRow {
  name: string;
  airport_code: string;
  zone_code: string;
  errors: string[];
  resolved_airport_id?: string;
  resolved_zone_id?: string;
}

export function HotelsSection() {
  const [rows, setRows] = useState<any[]>([]);
  const [zones, setZones] = useState<any[]>([]);
  const [airports, setAirports] = useState<any[]>([]);
  const [form, setForm] = useState({ name: "", zone_id: "", airport_id: "" });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [importData, setImportData] = useState<ParsedRow[] | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const load = async () => {
    const [h, z, a] = await Promise.all([
      supabase.from("hotels").select("*, zones(name, code), airports(code, name)"),
      supabase.from("zones").select("*"),
      supabase.from("airports").select("*"),
    ]);
    setRows(h.data || []);
    setZones(z.data || []);
    setAirports(a.data || []);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    const payload: any = { name: form.name };
    if (form.zone_id) payload.zone_id = form.zone_id;
    if (form.airport_id) payload.airport_id = form.airport_id;
    if (editingId) {
      const { error } = await supabase.from("hotels").update(payload).eq("id", editingId);
      if (error) {
        const msg = error.message.includes("hotels_name_airport_unique")
          ? "Ya existe un hotel con ese nombre para el aeropuerto seleccionado."
          : error.message;
        toast({ title: "Error", description: msg, variant: "destructive" });
        return;
      }
    } else {
      const { error } = await supabase.from("hotels").insert(payload);
      if (error) {
        const msg = error.message.includes("hotels_name_airport_unique")
          ? "Ya existe un hotel con ese nombre para el aeropuerto seleccionado."
          : error.message;
        toast({ title: "Error", description: msg, variant: "destructive" });
        return;
      }
    }
    setForm({ name: "", zone_id: "", airport_id: "" });
    setEditingId(null);
    setIsDialogOpen(false);
    load();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    await supabase.from("hotels").delete().eq("id", deleteId);
    setDeleteId(null);
    load();
  };

  const openCreate = () => { setForm({ name: "", zone_id: "", airport_id: "" }); setEditingId(null); setIsDialogOpen(true); };
  const openEdit = (row: any) => { setForm({ name: row.name, zone_id: row.zone_id || "", airport_id: row.airport_id || "" }); setEditingId(row.id); setIsDialogOpen(true); };

  // ── Excel: Download template ──
  const downloadTemplate = () => {
    const templateData = [
      { name: "Hotel Barceló", airport_code: "PUJ", zone_code: "BAVARO" },
      { name: "Hotel Iberostar", airport_code: "PUJ", zone_code: "UVERO" },
    ];
    const ws = XLSX.utils.json_to_sheet(templateData);
    ws["!cols"] = [{ wch: 30 }, { wch: 15 }, { wch: 15 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Hotels");
    XLSX.writeFile(wb, "plantilla_hoteles.xlsx");
  };

  // ── Excel: Parse & validate file ──
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target?.result;
        const wb = XLSX.read(data, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json: any[] = XLSX.utils.sheet_to_json(ws, { defval: "" });

        // Build set of existing hotels for duplicate detection
        const existingKeys = new Set(
          rows.map((h: any) => `${h.name.toLowerCase()}|${h.airport_id || ""}`)
        );

        // First pass: parse rows
        const parsed: ParsedRow[] = json.map((row) => {
          const name = String(row.name || row.Name || row.NOMBRE || row.nombre || "").trim();
          const airportCode = String(row.airport_code || row.Airport_Code || row.AIRPORT_CODE || "").trim().toUpperCase();
          const zoneCode = String(row.zone_code || row.Zone_Code || row.ZONE_CODE || "").trim().toUpperCase();

          const errors: string[] = [];
          if (!name) errors.push("Nombre vacío");

          const matchedAirport = airports.find((a) => a.code.toUpperCase() === airportCode);
          if (airportCode && !matchedAirport) errors.push(`Aeropuerto "${airportCode}" no existe`);

          const matchedZone = zones.find((z) => z.code.toUpperCase() === zoneCode);
          if (zoneCode && !matchedZone) errors.push(`Zona "${zoneCode}" no existe`);

          // Check duplicate against DB
          const dbKey = `${name.toLowerCase()}|${matchedAirport?.id || ""}`;
          if (name && existingKeys.has(dbKey)) errors.push("Ya existe en la base de datos");

          return {
            name,
            airport_code: airportCode,
            zone_code: zoneCode,
            errors,
            resolved_airport_id: matchedAirport?.id,
            resolved_zone_id: matchedZone?.id,
          };
        });

        // Second pass: detect intra-file duplicates
        const freq = new Map<string, number>();
        parsed.forEach((r) => {
          if (!r.name) return;
          const key = `${r.name.toLowerCase()}|${r.airport_code}`;
          freq.set(key, (freq.get(key) || 0) + 1);
        });
        parsed.forEach((r) => {
          if (!r.name) return;
          const key = `${r.name.toLowerCase()}|${r.airport_code}`;
          if ((freq.get(key) || 0) > 1 && !r.errors.includes("Duplicado en el archivo")) {
            r.errors.push("Duplicado en el archivo");
          }
        });

        setImportData(parsed);
      } catch {
        toast({ title: "Error", description: "No se pudo leer el archivo Excel.", variant: "destructive" });
      }
    };
    reader.readAsBinaryString(file);
    // Reset so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const validRows = importData?.filter((r) => r.errors.length === 0) || [];
  const invalidRows = importData?.filter((r) => r.errors.length > 0) || [];

  const handleImportValid = async () => {
    if (validRows.length === 0) return;
    setIsImporting(true);
    const payload = validRows.map((r) => ({
      name: r.name,
      airport_id: r.resolved_airport_id || null,
      zone_id: r.resolved_zone_id || null,
    }));
    const { error } = await supabase.from("hotels").insert(payload);
    setIsImporting(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Importación exitosa", description: `${validRows.length} hoteles importados.` });
    setImportData(null);
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold">Hoteles</h2>
          <p className="text-muted-foreground text-sm mt-1">Hoteles asociados a zonas <Badge variant="secondary" className="ml-2">{rows.length}</Badge></p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={downloadTemplate}><Download className="h-4 w-4 mr-1" /> Plantilla</Button>
          <Button variant="outline" onClick={() => fileInputRef.current?.click()}><Upload className="h-4 w-4 mr-1" /> Importar</Button>
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileChange} />
          <Button onClick={openCreate}><Plus className="h-4 w-4 mr-1" /> Agregar</Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Hotel className="h-10 w-10 mb-3 opacity-40" /><p>No hay hoteles registrados</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Aeropuerto</TableHead>
                  <TableHead>Zona</TableHead>
                  <TableHead className="w-[100px] text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.id}>
                    <TableCell className="font-medium">{row.name}</TableCell>
                    <TableCell>{(row.airports as any)?.code ? <Badge variant="outline">{(row.airports as any)?.code}</Badge> : "—"}</TableCell>
                    <TableCell>{(row.zones as any)?.code ? <Badge variant="outline">{(row.zones as any)?.code}</Badge> : "—"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(row)}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="text-destructive" onClick={() => setDeleteId(row.id)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingId ? "Editar Hotel" : "Nuevo Hotel"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nombre</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></div>
            <div>
              <Label>Aeropuerto</Label>
              <Select value={form.airport_id} onValueChange={v => setForm({ ...form, airport_id: v })}>
                <SelectTrigger><SelectValue placeholder="Seleccionar aeropuerto" /></SelectTrigger>
                <SelectContent>{airports.map(a => <SelectItem key={a.id} value={a.id}>{a.code} - {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Zona</Label>
              <Select value={form.zone_id} onValueChange={v => setForm({ ...form, zone_id: v })}>
                <SelectTrigger><SelectValue placeholder="Seleccionar zona" /></SelectTrigger>
                <SelectContent>{zones.map(z => <SelectItem key={z.id} value={z.id}>{z.code} - {z.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave}>{editingId ? "Actualizar" : "Crear"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Import Preview Dialog */}
      <Dialog open={!!importData} onOpenChange={(open) => !open && setImportData(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Vista previa de importación</DialogTitle>
            <DialogDescription>
              <span className="text-green-600 font-medium">{validRows.length} válidos</span>
              {invalidRows.length > 0 && <span className="text-destructive font-medium ml-3">{invalidRows.length} con errores</span>}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-auto flex-1 -mx-6 px-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Aeropuerto</TableHead>
                  <TableHead>Zona</TableHead>
                  <TableHead>Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {importData?.map((row, i) => (
                  <TableRow key={i} className={row.errors.length > 0 ? "bg-destructive/5" : "bg-green-500/5"}>
                    <TableCell>
                      {row.errors.length === 0
                        ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                        : <XCircle className="h-4 w-4 text-destructive" />}
                    </TableCell>
                    <TableCell className="font-medium">{row.name || <span className="text-muted-foreground italic">vacío</span>}</TableCell>
                    <TableCell>{row.airport_code || "—"}</TableCell>
                    <TableCell>{row.zone_code || "—"}</TableCell>
                    <TableCell>
                      {row.errors.length > 0
                        ? <span className="text-destructive text-xs">{row.errors.join(", ")}</span>
                        : <span className="text-green-600 text-xs">OK</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportData(null)}>Cancelar</Button>
            <Button onClick={handleImportValid} disabled={validRows.length === 0 || isImporting}>
              {isImporting ? "Importando..." : `Importar ${validRows.length} válidos`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>¿Eliminar hotel?</AlertDialogTitle><AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDelete}>Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
